import React, { FC, PropsWithChildren } from 'react';
import { Grid, Hidden, makeStyles } from '@dxb-web-ui-framework/design-system';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { CustomReactPortal } from '@dxb-web-ui-framework/common/components/CustomReactPortal/CustomReactPortal';
import { FormBuilderPresenter } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderPresenter';
import { FormBuilderTitle } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderTitle';
import { useWizardGoToNextStep } from '../../../contexts/ClientSideWizard/hooks';
import { ClientSideWizardStepProps } from '../../../contexts/ClientSideWizard/types';

export interface FormStepRestProps {
  title?: string;
  classes?: GenericObject;
  currentStep?: number;
  externalProps?: GenericObject;
}

const useStyles = makeStyles(theme => ({
  formStepWrapper: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
  },
  title: {
    color: theme.palette.common.white,
  },
}));

const FormStep: FC<PropsWithChildren<ClientSideWizardStepProps>> = ({
  contentsConfig,
  children,
  externalEffectCallbacks,
  ...restProps
}) => {
  // Read wizard step config
  const styles = useStyles();
  const goToNextWizardStep = useWizardGoToNextStep();

  const {
    portalId,
    components,
    customResolver,
    dataTestId,
    isCondensedForm,
    externalValidationErrors,
    preloadedData: preloadedFormDataConfig,
    submitDataAdapter,
    autoSubmit,
    validationSchema,
  } = contentsConfig || {};

  const { title, classes, externalProps }: FormStepRestProps = restProps || {};
  const { data, validationOptions, preloadDataAdapter } =
    (preloadedFormDataConfig as GenericObject<any> | undefined) || {};

  const formConfig = {
    components,
    customResolver,
    isCondensedForm,
    externalValidationErrors,
    preloadedData: data,
    preloadedDataValidationOptions: validationOptions,
    preloadDataAdapter,
    submitDataAdapter,
    autoSubmit,
    validationSchema,
  } as FormConfig;

  return (
    <Grid item container data-testid={`${dataTestId || 'wizard'}-step`} className={styles.formStepWrapper}>
      <FormBuilderPresenter
        formConfig={formConfig}
        externalProps={externalProps}
        submitAction={goToNextWizardStep}
        externalEffectCallbacks={externalEffectCallbacks}
        classes={classes}>
        <>
          {portalId && title && (
            <Hidden mdUp>
              <CustomReactPortal
                component={
                  <FormBuilderTitle customClass={styles.title} dataTestId={'portal-form-title'}>
                    {title}
                  </FormBuilderTitle>
                }
                elementId={portalId}
              />
            </Hidden>
          )}
          {title && (
            <Hidden smDown mdUp={!!children}>
              <FormBuilderTitle dataTestId={'form-title'}>{title}</FormBuilderTitle>
            </Hidden>
          )}
          {children}
        </>
      </FormBuilderPresenter>
    </Grid>
  );
};

export { FormStep };
